import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useTheme,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  HeadsetMic as HeadsetMicIcon,
  HourglassEmpty as HourglassIcon,
  SmartToy as BotIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Star as StarIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import type { DashboardAtendimentoStats, Atendimento, Atendente } from '../../types/atendimento';
import { atendimentoService } from '../../services/atendimentoService';

const EMPTY_STATS: DashboardAtendimentoStats = {
  total_atendendo: 0,
  total_aguardando: 0,
  total_bot: 0,
  total_finalizados_hoje: 0,
  tma_minutos: 0,
  satisfacao_media: 0,
  atendentes_online: 0,
};

// ── Helpers ──────────────────────────────────────────────────────────────────

const getInitials = (name: string) =>
  name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();

const statusConfig: Record<string, { label: string; color: 'success' | 'warning' | 'info' | 'default' }> = {
  atendendo: { label: 'Atendendo', color: 'success' },
  aguardando: { label: 'Aguardando', color: 'warning' },
  bot: { label: 'Bot', color: 'info' },
  finalizado: { label: 'Finalizado', color: 'default' },
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

// ── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  progress?: number;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, subtitle, icon, color, bgColor, progress }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={color}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: bgColor, width: 48, height: 48 }}>
          {icon}
        </Avatar>
      </Box>
      {progress !== undefined && (
        <Box mt={1.5}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 6, borderRadius: 3, bgcolor: `${bgColor}40`, '& .MuiLinearProgress-bar': { bgcolor: color } }}
          />
        </Box>
      )}
    </CardContent>
  </Card>
);

// ── Página ───────────────────────────────────────────────────────────────────

const AtendimentoDashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardAtendimentoStats>(EMPTY_STATS);
  const [atendimentosRecentes, setAtendimentosRecentes] = useState<Atendimento[]>([]);
  const [atendentes, setAtendentes] = useState<Atendente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregarDashboard = useCallback(async () => {
    try {
      setError(null);
      const data = await atendimentoService.getDashboard();
      setStats(data.stats);
      setAtendimentosRecentes(data.atendimentos_recentes as Atendimento[]);
      setAtendentes(data.atendentes);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Erro ao carregar dashboard de atendimento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarDashboard();
    const interval = window.setInterval(carregarDashboard, 30000);
    return () => window.clearInterval(interval);
  }, [carregarDashboard]);

  const total = stats.total_atendendo + stats.total_aguardando + stats.total_bot;
  const progressBase = total > 0 ? total : 1;
  const satisfacaoLabel = stats.satisfacao_media > 0
    ? `${stats.satisfacao_media}/5`
    : 'N/A';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <WhatsAppIcon sx={{ color: '#25d366', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Dashboard de Atendimento
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Visão geral dos atendimentos via WhatsApp
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPIs */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            title="Atendendo"
            value={stats.total_atendendo}
            icon={<HeadsetMicIcon sx={{ color: '#2e7d32' }} />}
            color="#2e7d32"
            bgColor="#e8f5e9"
            progress={(stats.total_atendendo / progressBase) * 100}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            title="Aguardando"
            value={stats.total_aguardando}
            subtitle="na fila"
            icon={<HourglassIcon sx={{ color: '#e65100' }} />}
            color="#e65100"
            bgColor="#fff3e0"
            progress={(stats.total_aguardando / progressBase) * 100}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            title="No Bot"
            value={stats.total_bot}
            icon={<BotIcon sx={{ color: '#0277bd' }} />}
            color="#0277bd"
            bgColor="#e1f5fe"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            title="Finalizados Hoje"
            value={stats.total_finalizados_hoje}
            icon={<CheckCircleIcon sx={{ color: '#1565c0' }} />}
            color="#1565c0"
            bgColor="#e3f2fd"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            title="TMA"
            value={`${stats.tma_minutos} min`}
            subtitle="tempo médio"
            icon={<AccessTimeIcon sx={{ color: '#6a1b9a' }} />}
            color="#6a1b9a"
            bgColor="#f3e5f5"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            title="Satisfação"
            value={satisfacaoLabel}
            icon={<StarIcon sx={{ color: '#f9a825' }} />}
            color="#f9a825"
            bgColor="#fffde7"
            progress={stats.satisfacao_media > 0 ? (stats.satisfacao_media / 5) * 100 : undefined}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Atendimentos Recentes */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                <TrendingUpIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />
                Atendimentos Recentes
              </Typography>
              <Chip label={`${total} em aberto`} size="small" color="primary" variant="outlined" />
            </Box>
            {atendimentosRecentes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                Nenhum atendimento em aberto no momento.
              </Typography>
            ) : (
              <List disablePadding>
                {atendimentosRecentes.map((at, idx) => (
                  <React.Fragment key={at.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem
                      alignItems="flex-start"
                      onClick={() => navigate('/atendimento/atendimentos', { state: { ticketId: at.id } })}
                      sx={{
                        px: 1,
                        py: 1,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: at.avatar_cor, width: 42, height: 42, fontSize: '0.85rem' }}>
                          {getInitials(at.nome_contato)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight={600}>
                              {at.nome_contato}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {at.mensagens_nao_lidas > 0 && (
                                <Chip
                                  label={at.mensagens_nao_lidas}
                                  size="small"
                                  color="error"
                                  sx={{ height: 18, fontSize: '0.65rem', minWidth: 20 }}
                                />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {at.ultima_mensagem_at && formatTime(at.ultima_mensagem_at)}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.3}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            >
                              {at.ultima_mensagem}
                            </Typography>
                            <Chip
                              label={statusConfig[at.status]?.label || at.status}
                              size="small"
                              color={statusConfig[at.status]?.color || 'default'}
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Atendentes */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="subtitle1" fontWeight={600}>
                <PeopleIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle', color: 'primary.main' }} />
                Atendentes
              </Typography>
              <Chip
                label={`${stats.atendentes_online} online`}
                size="small"
                color="success"
                variant="outlined"
              />
            </Box>
            {atendentes.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                Nenhum atendente vinculado aos setores.
              </Typography>
            ) : (
              <List disablePadding>
                {atendentes.map((atendente, idx) => (
                  <React.Fragment key={atendente.id}>
                    {idx > 0 && <Divider component="li" />}
                    <ListItem sx={{ px: 1, py: 0.8 }}>
                      <ListItemAvatar>
                        <Box position="relative" display="inline-flex">
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              fontSize: '0.8rem',
                              bgcolor: atendente.online ? 'primary.main' : 'action.disabledBackground',
                            }}
                          >
                            {getInitials(atendente.nome)}
                          </Avatar>
                          <CircleIcon
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: -2,
                              fontSize: 12,
                              color: atendente.online ? '#44b700' : theme.palette.text.disabled,
                            }}
                          />
                        </Box>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={atendente.online ? 600 : 400}>
                            {atendente.nome}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {atendente.setor || 'Sem setor'} · {atendente.online ? 'Online' : 'Offline'}
                          </Typography>
                        }
                      />
                      {atendente.online && (
                        <Chip label="Disponível" size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AtendimentoDashboardPage;
